import { Badge, Card, Group, Select, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { api, call } from "../api/client";
import { ErrorAlert, Loading, STATUS_COLORS, StatusBadge, formatTs } from "../components/common";

export const SCHEDULE_LABELS: Record<string, string> = {
  working: "in shift",
  break: "break",
  off_shift: "no shift",
  no_calendar: "no calendar",
};

const fmt = (v: unknown, unit = "") => (typeof v === "number" ? `${Math.round(v * 10) / 10}${unit}` : "—");

export function DashboardPage() {
  const [factoryId, setFactoryId] = useState<string | null>(null);
  const factories = useQuery({ queryKey: ["factories"], queryFn: () => call(api.GET("/api/v1/factories")) });
  const summary = useQuery({
    queryKey: ["dashboard", factoryId],
    queryFn: () =>
      call(api.GET("/api/v1/dashboard/summary", { params: { query: factoryId ? { factory_id: factoryId } : {} } })),
    refetchInterval: 30_000, // live updates trigger refreshes; polling is only the fallback
  });

  if (summary.isLoading) return <Loading />;
  if (summary.error) return <ErrorAlert error={summary.error} />;
  const data = summary.data!;

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Factory overview</Title>
        <Select placeholder="All factories" clearable value={factoryId} onChange={setFactoryId} w={220}
          data={(factories.data ?? []).map((f) => ({ value: f.id, label: f.name }))} />
      </Group>
      <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }}>
        <Card withBorder>
          <Text size="xs" c="dimmed">Machines</Text>
          <Text fw={700} size="xl">{data.machine_count}</Text>
        </Card>
        {["RUNNING", "IDLE", "SETUP", "FAULT", "OFFLINE"].map((s) => (
          <Card withBorder key={s}>
            <Text size="xs" c="dimmed">{s}</Text>
            <Text fw={700} size="xl" c={STATUS_COLORS[s]}>{data.status_counts[s] ?? 0}</Text>
          </Card>
        ))}
      </SimpleGrid>
      {data.machines.length === 0 && <Text c="dimmed">No machines registered yet.</Text>}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }}>
        {data.machines.map((m) => (
          <Card key={m.id} withBorder component={Link} to={`/machines/${m.id}`} style={{ textDecoration: "none" }}>
            <Group justify="space-between" mb="xs" wrap="nowrap">
              <div style={{ minWidth: 0 }}>
                <Text fw={600} truncate>{m.name}</Text>
                <Text size="xs" c="dimmed">{m.code} · {m.machine_type.replace("_", " ")}</Text>
              </div>
              <StatusBadge status={m.status} />
            </Group>
            <SimpleGrid cols={3} spacing="xs">
              <Metric label="Good today" value={String(m.good_today ?? 0)} />
              <Metric label="Rejected today" value={String(m.rejected_today ?? 0)} />
              <Metric label="In state since" value={m.state_since ? new Date(m.state_since).toLocaleTimeString() : "—"} />
              <Metric label="Temp" value={fmt(m.values.temperature, "°C")} />
              <Metric label="Seen" value={m.last_seen ? new Date(m.last_seen).toLocaleTimeString() : "—"} />
              <Metric label="Schedule" value={SCHEDULE_LABELS[m.schedule ?? "no_calendar"] ?? "—"} />
            </SimpleGrid>
            <Group gap={4} mt="xs">
              {m.is_simulated && <Badge size="xs" variant="outline" color="gray">simulated</Badge>}
              {(m.unclassified_downtime ?? 0) > 0 && (
                <Badge size="xs" color="orange" title="Automatically detected stops without a reason">
                  {m.unclassified_downtime} stop(s) need a reason
                </Badge>
              )}
              {m.status_source === "signal" && <Badge size="xs" variant="light" color="gray" title="No state rules: status signal shown as sent">signal status</Badge>}
            </Group>
          </Card>
        ))}
      </SimpleGrid>
      <Text size="xs" c="dimmed">Updated {formatTs(data.generated_at)} · updates live (polling every 30 s as fallback)</Text>
    </Stack>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm" fw={500}>{value}</Text>
    </div>
  );
}
